import React, { useCallback, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { apiFetch, callApi } from '../api';
import Modal from '../components/Modal';
import InfoTip from '../components/InfoTip';
import { useConfirm } from '../components/ConfirmDialog';
import {
  Badge, CardSkeleton, EmptyState, Field, Icon, PageHeader, Switch, TableSkeleton,
} from '../components/ui';
import usePhoneCalls from '../utils/usePhoneCalls';

// The Teams card's detail, kept in one place because it belongs to the channel rather
// than to any one field on it. Teams is the only channel here whose setup depends on
// values held in ANOTHER console, and both of those fail silently — hence a tip rather
// than four more lines of grey text under the table.
function TeamsInfoTip() {
  return (
    // Every one of these four is a SILENT failure, which is why none of them is cut and
    // only the words around them are. Kept out as recoverable: the App ID has to be
    // exactly the value on the Azure app registration, and the manifest key lives in its
    // `bots` section.
    <InfoTip label="About Teams setup" width={380}>
      <p>
        A wrong <strong>App ID</strong> resolves to no bot.
      </p>
      <p>
        <strong>Two Azure settings fail invisibly.</strong> Paste the messaging endpoint
        into the Azure Bot resource, on a publicly trusted cert, or delivery dies in the TLS
        handshake. Files need <code>supportsFiles: true</code> in the manifest, or Teams strips
        uploads while images keep working.
      </p>
      <p>
        Client secret expires, 24 months max. <strong>Account type</strong> must match the
        registration or every reply 401s.
      </p>
    </InfoTip>
  );
}

/* ── Shared pieces ──────────────────────────────────────────────────────── */

// A channel's credentials are managed by the section that owns them (the bot pool, the
// Teams app pool), but their one-line summary belongs in the card header, where it reads
// without scrolling. Sections report it through this rather than the page fetching the
// same lists a second time. The default is a no-op, so a section outside a card still works.
const SectionStatus = React.createContext(() => {});

// Decoration only: a channel missing here still gets its card, with the generic glyph.
const CHANNEL_ICON = {
  telegram: 'send', website: 'globe', teams: 'users', rossapp: 'message', 'rossapp-pen': 'message',
  voice: 'phone',
};

// The Ross app's channels, one per app backend, each with its own connection card.
const APP_CHANNELS = ['rossapp', 'rossapp-pen'];

const plural = (n, noun) => `${n} ${noun}${n === 1 ? '' : 's'}`;

// The heading row every configuration section opens with: what it is, one line on how it
// behaves, and its action.
function SectionHead({ title, action, children }) {
  return (
    <div className="row row-top gap-3 mb-4">
      <div className="grow">
        <h3 className="text-base fw-600">{title}</h3>
        {children && <p className="text-sm text-muted mt-1">{children}</p>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

// A read that failed is not an empty pool. Rendering it as one invites registering a
// credential that is already there, so each pool says the list could not be read instead.
function LoadError({ children }) {
  return (
    <div className="notice notice-danger" role="alert">
      <Icon name="alertCircle" />
      <div className="notice-body">{children}</div>
    </div>
  );
}

/* ── Bot Pool ───────────────────────────────────────────────────────────── */

function BotModal({ mode, initial, onSubmit, onCancel, busy }) {
  const [username, setUsername] = useState(initial?.username || '');
  const [token, setToken] = useState('');
  const creating = mode === 'create';

  const submit = (e) => {
    e.preventDefault();
    onSubmit({ username: username.trim(), token: token.trim() });
  };

  return (
    <Modal open onClose={onCancel} maxWidth={440}
           title={creating ? 'Add Telegram bot' : `Edit @${initial.username}`}
           description={creating ? 'BotFather issues the token when the bot is created.' : undefined}>
      <form onSubmit={submit}>
        <Field
          label="Bot handle"
          htmlFor="bot-handle"
          required
          hint={<>The @ is optional: the handle is stored without it, in lowercase, and links use <code>https://t.me/&lt;handle&gt;</code>.</>}
        >
          <input
            id="bot-handle"
            className="form-input"
            value={username}
            onChange={e => setUsername(e.target.value)}
            placeholder="@RossAIBot"
            autoFocus={creating}
            autoComplete="off"
            spellCheck={false}
            required
          />
        </Field>
        <Field
          label="Bot token"
          htmlFor="bot-token"
          required={creating}
          extra={!creating && <span className="fw-400 text-muted">(leave empty to keep current)</span>}
        >
          {/* new-password: a browser must never autofill the console login into a field
              whose value is sent on save. On an edit that would silently rotate the token. */}
          <input
            id="bot-token"
            className="form-input"
            type="password"
            value={token}
            onChange={e => setToken(e.target.value)}
            placeholder="123456:ABCdef…"
            autoComplete="new-password"
            required={creating}
          />
        </Field>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn-primary"
                  disabled={busy || !username.trim() || (creating && !token.trim())}>
            {busy ? <><span className="spinner-sm" /> Saving…</> : (creating ? 'Add bot' : 'Save changes')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function TelegramBotPool() {
  const [bots, setBots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [modal, setModal] = useState(null);   // { mode: 'create'|'edit', initial?: bot }
  const [busy, setBusy] = useState(false);
  const [confirm, confirmDialog] = useConfirm();
  const reportStatus = useContext(SectionStatus);

  const fetchBots = async () => {
    try {
      const resp = await apiFetch('/api/telegram-bots');
      if (resp.ok) {
        setBots(await resp.json());
        setLoadError(null);
      } else {
        setLoadError(`The bot pool could not be loaded (${resp.status}).`);
      }
    } catch (e) {
      setLoadError('The bot pool could not be loaded.');
    }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchBots(); }, []);

  // An empty pool is reported as a count, not as "not configured": the bridge falls back to
  // the shared default bot (config_defaults.default_bot_token) when the pool is empty.
  useEffect(() => {
    if (loading) return;
    reportStatus('telegram', loadError ? null
      : bots.length ? { tone: 'ok', text: plural(bots.length, 'bot') }
        : { tone: 'neutral', text: 'No bots' });
  }, [loading, loadError, bots, reportStatus]);

  const handleCreate = async ({ username, token }) => {
    setBusy(true);
    try {
      const resp = await apiFetch('/api/telegram-bots', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, token }),
      });
      if (resp.ok) {
        toast.success(`Added @${username.replace(/^@/, '').toLowerCase()}`);
        setModal(null);
        fetchBots();
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to add bot');
      }
    } finally { setBusy(false); }
  };

  const handleUpdate = async ({ username, token }) => {
    setBusy(true);
    try {
      const body = { username };
      if (token) body.token = token;
      const resp = await apiFetch(`/api/telegram-bots/${modal.initial.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (resp.ok) {
        toast.success('Bot updated');
        setModal(null);
        fetchBots();
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update bot');
      }
    } finally { setBusy(false); }
  };

  const handleDelete = async (bot) => {
    if (!await confirm({
      title: `Delete @${bot.username}?`,
      // Only a bot with no users reaches this dialog: the button is disabled while any
      // non-deleted user is assigned, and the backend refuses with 409 if one is.
      body: <p>This cannot be undone. No user is assigned to this bot.</p>,
      confirmLabel: 'Delete bot',
    })) return;
    const resp = await apiFetch(`/api/telegram-bots/${bot.id}`, { method: 'DELETE' });
    if (resp.ok) {
      toast.success(`Deleted @${bot.username}`);
      fetchBots();
    } else {
      const err = await resp.json().catch(() => ({}));
      toast.error(err.detail || 'Failed to delete bot');
    }
  };

  const openCreate = () => setModal({ mode: 'create' });

  return (
    <div className="card-section">
      <SectionHead
        title="Bot pool"
        action={(
          <button type="button" className="btn btn-secondary btn-sm" onClick={openCreate}>
            <Icon name="plus" /> Add bot
          </button>
        )}
      >
        New users are auto-assigned to the bot with the fewest active users. Rotating a bot's
        handle or token keeps existing users linked via the internal ID.
      </SectionHead>

      {loadError && <LoadError>{loadError}</LoadError>}

      {loading ? (
        <TableSkeleton rows={3} cols={4} label="Loading bots" />
      ) : bots.length === 0 ? (
        !loadError && (
          <EmptyState
            icon="bot"
            title="No bots in the pool yet"
            action={<button type="button" className="btn btn-primary" onClick={openCreate}>Add bot</button>}
          >
            Register a bot with its handle and the token BotFather issued for it.
          </EmptyState>
        )
      ) : (
        <div className="table-container">
          <table className="table table-stack">
            <thead>
              <tr>
                <th>Handle</th>
                <th>Token</th>
                <th className="num">Users</th>
                <th><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {bots.map(bot => {
                // A bot still carrying users cannot be deleted: reassign them first.
                const inUse = (bot.user_count ?? 0) > 0;
                return (
                  <tr key={bot.id}>
                    <td data-label="Handle">
                      <a href={`https://t.me/${bot.username}`} target="_blank" rel="noreferrer"
                         className="cell-primary row gap-1">
                        @{bot.username}
                        <Icon name="external" size={12} className="text-faint" />
                        <span className="sr-only"> (opens in a new tab)</span>
                      </a>
                    </td>
                    <td data-label="Token"><code>{bot.token_masked}</code></td>
                    <td data-label="Users" className="num">{bot.user_count ?? 0}</td>
                    <td data-label="Actions">
                      <div className="row row-end gap-1">
                        <button type="button" className="btn btn-ghost btn-sm btn-icon"
                                onClick={() => setModal({ mode: 'edit', initial: bot })}
                                aria-label={`Edit @${bot.username}`} title="Edit bot">
                          <Icon name="edit" />
                        </button>
                        <button type="button" className="btn btn-danger-soft btn-sm btn-icon"
                                disabled={inUse}
                                aria-label={inUse
                                  ? `Delete @${bot.username}, unavailable while users are connected`
                                  : `Delete @${bot.username}`}
                                title={inUse ? 'Users still connected, reassign them first' : 'Delete bot'}
                                onClick={() => handleDelete(bot)}>
                          <Icon name="trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <BotModal
          mode={modal.mode}
          initial={modal.initial}
          busy={busy}
          onCancel={() => setModal(null)}
          onSubmit={modal.mode === 'create' ? handleCreate : handleUpdate}
        />
      )}

      {confirmDialog}
    </div>
  );
}

/* ── Microsoft Teams ────────────────────────────────────────────────────── */

// Teams is a WEBHOOK channel: the Bot Connector POSTs activities to us, so the operator
// needs two things from this page — somewhere to put the Azure Bot's credentials, and the
// messaging endpoint to paste back into Azure.
//
// The endpoint is an OUTPUT, not a setting. Its path comes from the backend (one home,
// shared with the terraform listener rule); the host is this console's own origin, because
// the same ALB listener serves both. A `url` from the backend overrides that, for the case
// where the webhook is fronted by a different hostname.
// Hostnames Azure Bot Service cannot deliver to. It requires a publicly trusted TLS
// certificate, and an AWS-assigned load-balancer name or a private domain can never have one
// — the handshake fails before any request exists, so the bot appears to do nothing and there
// is no log line anywhere to explain it. On DEV this cost a full debugging round: the only
// trace was ClientTLSNegotiationErrorCount on the load balancer.
//
// Checked here because this box is where the operator gets the value they paste into Azure.
// Publishing an endpoint we know cannot work, with no warning, is what made it expensive.
const UNREACHABLE_HOST = /(\.elb\.amazonaws\.com|\.internal|^localhost$|^127\.|^\d+\.\d+\.\d+\.\d+$)/i;

function WebhookEndpoint({ webhook }) {
  const [copied, setCopied] = useState(false);
  if (!webhook) return null;
  // `webhook.url` is the backend's teams_webhook_base_url override; absent, the console's own
  // origin is correct by construction, because the same ALB listener serves both paths.
  const url = webhook.url || `${window.location.origin}${webhook.path}`;
  let unreachable = false;
  try {
    unreachable = UNREACHABLE_HOST.test(new URL(url).hostname);
  } catch { /* an unparseable URL is not worth blocking the page over */ }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard is unavailable over plain http and in some locked-down browsers. The
      // value is selectable text either way, so this is a convenience failing, not a
      // blocked task — say so instead of throwing a red toast at it.
      toast.info('Copy is unavailable here, select the URL manually');
    }
  };

  return (
    <>
      <SectionHead title="Messaging endpoint">
        Paste into the Azure Bot resource, under <b>Configuration → Messaging endpoint</b>.
      </SectionHead>

      {/* Above the value, not under it: this is the moment before the operator copies it. */}
      {unreachable && (
        <div className="notice notice-warn">
          <Icon name="alert" />
          <div className="notice-body">
            <div className="notice-title">Teams cannot deliver to this hostname</div>
            <p className="mt-1">
              Azure Bot Service requires a publicly trusted TLS certificate, and this host
              cannot have one: the connection fails during the TLS handshake, so the bot goes
              silent with nothing in any log to explain it. Reach this console at its public
              hostname, or set the <code>teams_webhook_base_url</code> setting, and copy the
              endpoint again.
            </p>
          </div>
        </div>
      )}

      {/* Scrolls sideways rather than truncating, so the whole URL stays selectable by hand. */}
      <div className="row gap-2">
        <code className="code-block grow">{url}</code>
        <button type="button" className="btn btn-secondary btn-sm" onClick={copy}>
          <Icon name={copied ? 'check' : 'copy'} />
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <span className="sr-only" aria-live="polite">{copied ? 'Messaging endpoint copied' : ''}</span>
    </>
  );
}

function TeamsAppModal({ mode, initial, onSubmit, onCancel, busy }) {
  const [displayName, setDisplayName] = useState(initial?.display_name || '');
  const [appId, setAppId] = useState(initial?.app_id || '');
  const [appPassword, setAppPassword] = useState('');
  const [appType, setAppType] = useState(initial?.app_type || 'SingleTenant');
  const [tenantId, setTenantId] = useState(initial?.azure_tenant_id || '');

  const singleTenant = appType === 'SingleTenant';

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      display_name: displayName.trim(),
      app_id: appId.trim(),
      app_password: appPassword.trim(),
      app_type: appType,
      // Sent even when blank on a multi-tenant app: the backend clears it, because a
      // tenant id the token URL ignores reads to the next operator as working config.
      azure_tenant_id: singleTenant ? tenantId.trim() : '',
    });
  };

  return (
    <Modal open onClose={onCancel} maxWidth={520}
           title={mode === 'create' ? 'Add Teams app' : `Edit ${initial.display_name}`}
           description={mode === 'create'
             ? "The IDs and the secret come from the bot's app registration in Microsoft Entra."
             : undefined}>
      <form onSubmit={submit}>
        <Field label="Name" htmlFor="teams-app-name" required
               hint="A label for this page only. Not shown to users.">
          <input id="teams-app-name" className="form-input" value={displayName}
                 onChange={e => setDisplayName(e.target.value)}
                 placeholder="Fastweb Corporate"
                 autoFocus={mode === 'create'} required />
        </Field>

        <Field label="Microsoft App ID" htmlFor="teams-app-id" required
               hint={<>The app registration's <b>Application (client) ID</b>. Also how an incoming Teams
                 message is matched to this row, so it must be exact.</>}>
          <input id="teams-app-id" className="form-input mono" value={appId}
                 onChange={e => setAppId(e.target.value)}
                 placeholder="00000000-0000-0000-0000-000000000000"
                 autoComplete="off" spellCheck={false} required />
        </Field>

        <Field label="Client secret" htmlFor="teams-app-secret" required={mode === 'create'}
               extra={mode === 'edit' && <span className="fw-400 text-muted">(leave empty to keep current)</span>}
               hint="Entra client secrets expire, 24 months at most. Rotate it here; no deploy needed.">
          {/* new-password for the same reason as the bot token: never autofill a login here. */}
          <input id="teams-app-secret" className="form-input" type="password" value={appPassword}
                 onChange={e => setAppPassword(e.target.value)}
                 placeholder="Entra client secret"
                 autoComplete="new-password"
                 required={mode === 'create'} />
        </Field>

        <Field label="App type" htmlFor="teams-app-type"
               hint={<>Must match <b>Supported account types</b> on the app registration. A mismatch
                 fails every outgoing reply with a 401 and nothing else.</>}>
          <select id="teams-app-type" className="form-input" value={appType}
                  onChange={e => setAppType(e.target.value)}>
            <option value="SingleTenant">Single tenant</option>
            <option value="MultiTenant">Multi tenant</option>
          </select>
        </Field>

        {singleTenant && (
          <Field label="Directory (tenant) ID" htmlFor="teams-tenant-id" required
                 hint="The Entra directory hosting the app. A different GUID from the App ID above.">
            <input id="teams-tenant-id" className="form-input mono" value={tenantId}
                   onChange={e => setTenantId(e.target.value)}
                   placeholder="00000000-0000-0000-0000-000000000000"
                   autoComplete="off" spellCheck={false} required />
          </Field>
        )}

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn-primary"
                  disabled={busy || !displayName.trim() || !appId.trim()
                            || (mode === 'create' && !appPassword.trim())
                            || (singleTenant && !tenantId.trim())}>
            {busy ? <><span className="spinner-sm" /> Saving…</> : (mode === 'create' ? 'Add app' : 'Save changes')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function TeamsAppPool() {
  const [apps, setApps] = useState([]);
  const [linkedUsers, setLinkedUsers] = useState(0);
  const [webhook, setWebhook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [modal, setModal] = useState(null);   // { mode: 'create'|'edit', initial?: app }
  const [busy, setBusy] = useState(false);
  const [confirm, confirmDialog] = useConfirm();
  const reportStatus = useContext(SectionStatus);

  const fetchApps = async () => {
    try {
      const resp = await apiFetch('/api/teams-apps');
      if (resp.ok) {
        const data = await resp.json();
        setApps(data.apps || []);
        setLinkedUsers(data.linked_users || 0);
        setLoadError(null);
      } else {
        setLoadError(`The Teams apps could not be loaded (${resp.status}).`);
      }
    } catch (e) {
      setLoadError('The Teams apps could not be loaded.');
    }
    finally { setLoading(false); }
  };

  useEffect(() => {
    fetchApps();
    // The endpoint does not change, so it is fetched once and never refreshed with the list.
    (async () => {
      try {
        const resp = await apiFetch('/api/teams-apps/webhook');
        if (resp.ok) setWebhook(await resp.json());
      } catch { /* the card is still usable without it */ }
    })();
  }, []);

  // Only enabled rows count: the bridge authenticates against enabled registrations alone,
  // so a pool of disabled rows accepts nothing.
  useEffect(() => {
    if (loading) return;
    const live = apps.filter(app => app.enabled).length;
    reportStatus('teams', loadError ? null
      : live ? { tone: 'ok', text: plural(live, 'app') }
        : apps.length ? { tone: 'warn', text: 'All apps disabled' }
          : { tone: 'neutral', text: 'Not configured' });
  }, [loading, loadError, apps, reportStatus]);

  const save = async (body) => {
    setBusy(true);
    try {
      const editing = modal.mode === 'edit';
      const payload = { ...body };
      // Omit rather than send empty: the backend reads an absent secret as "keep the
      // current one", and an empty string would be a validation error on every edit that
      // was not a rotation.
      if (editing && !payload.app_password) delete payload.app_password;
      const resp = await apiFetch(
        editing ? `/api/teams-apps/${modal.initial.id}` : '/api/teams-apps',
        {
          method: editing ? 'PATCH' : 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      );
      if (resp.ok) {
        toast.success(editing ? 'Teams app updated' : `Added ${body.display_name}`);
        setModal(null);
        fetchApps();
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to save Teams app');
      }
    } finally { setBusy(false); }
  };

  const handleDelete = async (app) => {
    if (!await confirm({
      title: `Delete ${app.display_name}?`,
      body: (
        <>
          <p>
            Teams messages authenticated with this App ID stop being accepted, and
            replies to users on it stop being delivered.
          </p>
          {linkedUsers > 0 && (
            <p>
              <strong>
                {linkedUsers} user{linkedUsers === 1 ? '' : 's'} currently linked on Teams.
              </strong>
            </p>
          )}
        </>
      ),
      confirmLabel: 'Delete Teams app',
    })) return;
    const resp = await apiFetch(`/api/teams-apps/${app.id}`, { method: 'DELETE' });
    if (resp.ok) {
      toast.success(`Deleted ${app.display_name}`);
      fetchApps();
    } else {
      const err = await resp.json().catch(() => ({}));
      toast.error(err.detail || 'Failed to delete Teams app');
    }
  };

  const openCreate = () => setModal({ mode: 'create' });

  return (
    <>
      {webhook && (
        <div className="card-section">
          <WebhookEndpoint webhook={webhook} />
        </div>
      )}

      <div className="card-section">
        <SectionHead
          title="Teams apps"
          action={(
            <button type="button" className="btn btn-secondary btn-sm" onClick={openCreate}>
              <Icon name="plus" /> Add app
            </button>
          )}
        >
          One row per Azure Bot registration, so several organisations can each have
          their own bot.
          {linkedUsers > 0 && <> {plural(linkedUsers, 'user')} currently linked on Teams.</>}
        </SectionHead>

        {loadError && <LoadError>{loadError}</LoadError>}

        {loading ? (
          <TableSkeleton rows={2} cols={5} label="Loading Teams apps" />
        ) : apps.length === 0 ? (
          !loadError && (
            <EmptyState
              icon="key"
              title="No Teams app registered yet"
              action={<button type="button" className="btn btn-primary" onClick={openCreate}>Add app</button>}
            >
              Add an Azure Bot registration's App ID and client secret to connect one.
            </EmptyState>
          )
        ) : (
          <div className="table-container">
            <table className="table table-stack">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>App ID</th>
                  <th>Type</th>
                  <th>Secret</th>
                  <th><span className="sr-only">Actions</span></th>
                </tr>
              </thead>
              <tbody>
                {apps.map(app => (
                  <tr key={app.id}>
                    <td data-label="Name">
                      <div className="row-wrap gap-2">
                        <span className={app.enabled ? 'cell-primary' : 'fw-500 text-muted'}>
                          {app.display_name}
                        </span>
                        {!app.enabled && <Badge tone="neutral">Disabled</Badge>}
                      </div>
                    </td>
                    <td data-label="App ID"><code className="text-xs">{app.app_id}</code></td>
                    <td data-label="Type" className="nowrap">
                      {app.app_type === 'MultiTenant' ? 'Multi tenant' : 'Single tenant'}
                    </td>
                    <td data-label="Secret"><code>{app.app_password_masked}</code></td>
                    <td data-label="Actions">
                      <div className="row row-end gap-1">
                        <button type="button" className="btn btn-ghost btn-sm btn-icon"
                                onClick={() => setModal({ mode: 'edit', initial: app })}
                                aria-label={`Edit ${app.display_name}`} title="Edit Teams app">
                          <Icon name="edit" />
                        </button>
                        <button type="button" className="btn btn-danger-soft btn-sm btn-icon"
                                onClick={() => handleDelete(app)}
                                aria-label={`Delete ${app.display_name}`} title="Delete Teams app">
                          <Icon name="trash" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <TeamsAppModal
          mode={modal.mode}
          initial={modal.initial}
          busy={busy}
          onCancel={() => setModal(null)}
          onSubmit={save}
        />
      )}

      {confirmDialog}
    </>
  );
}

/* ── Ross App connection ────────────────────────────────────────────────── */

function AppConnectionModal({ channel, label, initial, onSubmit, onCancel, busy }) {
  const [apiUrl, setApiUrl] = useState(initial?.api_url || '');
  const [apiToken, setApiToken] = useState('');
  const hasToken = Boolean(initial?.api_token_masked);

  const submit = (e) => {
    e.preventDefault();
    onSubmit({ api_url: apiUrl.trim(), api_token: apiToken.trim() });
  };

  return (
    <Modal open onClose={onCancel} maxWidth={520} title={`${label} connection`}
           description="Both values come from the mobile app's backend team.">
      <form onSubmit={submit}>
        <Field label="Endpoint" htmlFor={`${channel}-api-url`} required
               hint={<>The app backend's base URL, without <code>/ross</code>. The bridge calls
                 <code>&lt;endpoint&gt;/ross/…</code>.</>}>
          <input id={`${channel}-api-url`} className="form-input mono" value={apiUrl}
                 onChange={e => setApiUrl(e.target.value)}
                 placeholder="https://api.example.it"
                 autoComplete="off" spellCheck={false} autoFocus required />
        </Field>

        <Field label="Token" htmlFor={`${channel}-api-token`} required={!hasToken}
               extra={hasToken && <span className="fw-400 text-muted">(leave empty to keep current)</span>}
               hint="One of the values in the app's ROSS_CHANNEL_TOKENS. Rotate it here; no deploy needed.">
          {/* new-password for the same reason as the bot token: never autofill a login here. */}
          <input id={`${channel}-api-token`} className="form-input" type="password" value={apiToken}
                 onChange={e => setApiToken(e.target.value)}
                 placeholder="Bearer token"
                 autoComplete="new-password"
                 required={!hasToken} />
        </Field>

        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn-primary"
                  disabled={busy || !apiUrl.trim() || (!hasToken && !apiToken.trim())}>
            {busy ? <><span className="spinner-sm" /> Saving…</> : 'Save connection'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// The app backend an app channel's bridge polls: app-bridge for `rossapp`, app-pen-bridge for
// `rossapp-pen`. One endpoint and one token per channel, held in the database like the Teams
// credentials, so a rotation needs no deploy: the bridge re-reads it each minute.
function AppConnection({ channel = 'rossapp', label = 'Ross App' }) {
  const [cfg, setCfg] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [modal, setModal] = useState(false);
  const [busy, setBusy] = useState(false);
  const reportStatus = useContext(SectionStatus);
  const endpoint = `/api/app-channel?channel=${encodeURIComponent(channel)}`;

  const load = async () => {
    try {
      const resp = await apiFetch(endpoint);
      if (resp.ok) {
        setCfg(await resp.json());
        setLoadError(null);
      } else {
        setLoadError(`The ${label} connection could not be loaded (${resp.status}).`);
      }
    } catch {
      setLoadError(`The ${label} connection could not be loaded.`);
    }
  };

  useEffect(() => { load(); }, [channel]);

  useEffect(() => {
    if (!cfg && !loadError) return;
    reportStatus(channel, loadError ? null
      : !cfg.configured ? { tone: 'neutral', text: 'Not configured' }
        : cfg.enabled ? { tone: 'ok', text: 'Polling' }
          : { tone: 'warn', text: 'Paused' });
  }, [cfg, loadError, reportStatus, channel]);

  const update = async (body, successMsg) => {
    setBusy(true);
    try {
      const payload = { ...body };
      // Omit rather than send empty: an absent token keeps the current one.
      if (!payload.api_token) delete payload.api_token;
      const resp = await apiFetch(endpoint, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (resp.ok) {
        setCfg(await resp.json());
        setModal(false);
        toast.success(successMsg);
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || `Failed to save the ${label} connection`);
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="card-section">
      <SectionHead
        title="Connection"
        action={cfg && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setModal(true)}>
            <Icon name="edit" /> {cfg.configured ? 'Edit' : 'Configure'}
          </button>
        )}
      >
        The mobile app backend the bridge polls for messages and delivers replies to.
      </SectionHead>

      {loadError && <LoadError>{loadError}</LoadError>}

      {!cfg && !loadError ? (
        <TableSkeleton rows={2} cols={2} label={`Loading the ${label} connection`} />
      ) : cfg && (
        <>
          <dl className="kv">
            <dt>Endpoint</dt>
            <dd>{cfg.api_url ? <code className="text-xs">{cfg.api_url}</code>
              : <span className="text-faint">Not set</span>}</dd>
            <dt>Token</dt>
            <dd>{cfg.api_token_masked ? <code>{cfg.api_token_masked}</code>
              : <span className="text-faint">Not set</span>}</dd>
          </dl>
          <div className="setting-row p-0 mt-4">
            <div className="grow">
              <label className="setting-row-title" htmlFor={`${channel}-polling`}>Poll the app</label>
              <p className="setting-row-desc">
                Off pauses new app messages. Replies to messages already received still go out.
              </p>
            </div>
            <div className="row gap-2 shrink-0">
              {busy && <span className="spinner-sm text-muted" aria-hidden="true" />}
              <Switch id={`${channel}-polling`} checked={cfg.enabled}
                      disabled={busy || (!cfg.configured && !cfg.enabled)}
                      onChange={(next) => update({ enabled: next },
                        next ? `${label} polling on` : `${label} polling paused`)} />
            </div>
          </div>
        </>
      )}

      {modal && cfg && (
        <AppConnectionModal
          channel={channel}
          label={label}
          initial={cfg}
          busy={busy}
          onCancel={() => setModal(false)}
          onSubmit={(body) => update(body, `${label} connection saved`)}
        />
      )}
    </div>
  );
}

/* ── Coupon policy ──────────────────────────────────────────────────────── */

// Whether a channel accepts coupon codes for self-onboarding. Coupon redemption CREATES a
// user, so this is the switch that decides whether anyone holding a valid code can open an
// account from that channel, or whether every account there has to be created by an operator
// or by a producer through the API.
//
// Default is ON, and a channel that has never been configured reports `explicit: false`. The
// backend treats an absent key as enabled, so shipping this page cannot close a channel that
// was already accepting codes.
//
// Enforcement is in the backend (services/coupons.py reads the policy on every redemption),
// not here and not in proxy-channels — this control only writes the value.
function CouponToggle({ channel, label, policy, onChange }) {
  const [busy, setBusy] = useState(false);
  // Bumped to force a re-render when a confirm is declined. The checkbox is controlled from
  // `policy`, and returning early changes no state, so without this React never re-renders
  // and the box stays visually flipped while the policy says the opposite.
  const [, resync] = useState(0);
  const [confirm, confirmDialog] = useConfirm();
  const entry = policy?.channels?.[channel];
  const enabled = entry ? entry.coupons_enabled : true;
  const shown = label || channel;
  const switchId = `coupons-${channel}`;

  const toggle = async (next) => {
    if (!next && !await confirm({
      title: `Stop accepting coupon codes on ${shown}?`,
      body: (
        <>
          <p>
            People sending a valid code to this channel will no longer get an account.
          </p>
          <p>
            Existing users are unaffected, and accounts can still be created by an
            operator or through the producer API.
          </p>
        </>
      ),
      confirmLabel: 'Stop accepting codes',
    })) {
      resync(n => n + 1);
      return;
    }
    setBusy(true);
    try {
      const resp = await apiFetch('/api/channel-policy', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ channel, coupons_enabled: next }),
      });
      if (resp.ok) {
        const data = await resp.json();
        onChange(data);
        toast.success(next
          ? `Coupon activation enabled on ${shown}`
          : `Coupon activation disabled on ${shown}`);
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(err.detail || 'Failed to update coupon policy');
        resync(n => n + 1);   // the write failed, so put the box back
      }
    } catch {
      toast.error('Failed to update coupon policy');
      resync(n => n + 1);
    } finally { setBusy(false); }
  };

  return (
    <div className="setting-row p-0">
      <div className="grow">
        <label className="setting-row-title" htmlFor={switchId}>
          Accept coupon codes for activation
        </label>
        <p className="setting-row-desc">
          When on, anyone who sends a valid coupon code to this channel gets an account created
          against it. When off, codes sent here are ignored and accounts must be created by an
          operator or through the producer API. Takes effect on the next attempt.
          {entry && entry.explicit && entry.updated_at && (
            <> Last changed {new Date(entry.updated_at).toLocaleString()}.</>
          )}
          {entry && !entry.explicit && <> Currently at the default.</>}
        </p>
      </div>
      <div className="row gap-2 shrink-0">
        {busy && <span className="spinner-sm text-muted" aria-hidden="true" />}
        <Switch id={switchId} checked={enabled} disabled={busy || !policy} onChange={toggle} />
      </div>

      {confirmDialog}
    </div>
  );
}

/* ── Website dead letters ───────────────────────────────────────────────── */

// A website message the bridge could not deliver, even after retrying for minutes, is parked
// rather than dropped, and nothing re-sends it on its own. A superadmin does, from here: the
// button only marks rows, and the bridge re-sends them within a minute and records the
// outcome (WB2). The message text is never shown here; it is the user's.
function WebsiteDeadLetters() {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const resp = await apiFetch('/api/website-deadletters?limit=50');
      if (!resp.ok) {
        setLoadError('Could not read the parked messages.');
        return;
      }
      setData(await resp.json());
      setLoadError(null);
    } catch {
      setLoadError('Could not read the parked messages.');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const redrive = async (body) => {
    setBusy(true);
    try {
      const out = await callApi('/api/website-deadletters/redrive', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }, { errorMsg: 'Redrive request failed' });
      if (out) {
        toast.success(out.requested
          ? `${plural(out.requested, 'message')} queued; the bridge re-sends within a minute`
          : 'Nothing left to redrive');
        load();
      }
    } finally {
      setBusy(false);
    }
  };

  const items = data?.items || [];
  const open = data?.open || 0;
  // Queued rows are open until the bridge has re-sent them, and asking again changes nothing.
  const redrivable = open - (data?.pending || 0);
  return (
    <div className="card-section">
      <SectionHead
        title="Parked messages"
        action={redrivable > 0 && (
          <button type="button" className="btn btn-secondary btn-sm" disabled={busy}
                  onClick={() => redrive({ all: true })}>
            <Icon name="refresh" /> Redrive all
          </button>
        )}
      >
        Messages the bridge could not deliver after retrying.
        {data && <> {plural(open, 'message')} waiting{data.pending ? `, ${data.pending} queued for redrive` : ''}.</>}
      </SectionHead>

      {loadError && <LoadError>{loadError}</LoadError>}

      {!data && !loadError ? (
        <TableSkeleton rows={2} cols={4} label="Loading parked messages" />
      ) : items.length === 0 ? (
        !loadError && (
          <EmptyState icon="inbox" title="No parked messages">
            Every website message so far was delivered.
          </EmptyState>
        )
      ) : (
        <div className="table-container">
          <table className="table table-stack">
            <thead>
              <tr>
                <th>Parked</th>
                <th>Account</th>
                <th>Reason</th>
                <th>Status</th>
                <th><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => {
                const status = row.redriven_at
                  ? <Badge tone="ok">Delivered {new Date(row.redriven_at).toLocaleString()}</Badge>
                  : row.redrive_requested_at
                    ? <Badge tone="info">Queued</Badge>
                    : row.last_redrive_error
                      ? <Badge tone="danger" title={row.last_redrive_error}>Redrive failed</Badge>
                      : <Badge tone="outline">Parked</Badge>;
                return (
                  <tr key={row.id}>
                    <td data-label="Parked" className="nowrap">
                      {new Date(row.parked_at).toLocaleString()}
                    </td>
                    <td data-label="Account"><code className="text-xs">{row.account_ref}</code></td>
                    <td data-label="Reason" className="text-sm">{row.reason}</td>
                    <td data-label="Status">{status}</td>
                    <td data-label="Actions">
                      {!row.redriven_at && !row.redrive_requested_at && (
                        <div className="row row-end">
                          <button type="button" className="btn btn-ghost btn-sm btn-icon"
                                  disabled={busy} onClick={() => redrive({ ids: [row.id] })}
                                  aria-label={`Redrive message ${row.message_id}`}
                                  title="Redrive this message">
                            <Icon name="refresh" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ── Page ───────────────────────────────────────────────────────────────── */

function Channels() {
  const [policy, setPolicy] = useState(null);
  // The Phone card only where the environment takes calls (voice_channel_enabled).
  const phoneOn = usePhoneCalls();
  // A failed load leaves every toggle disabled. Without saying why, that looks like the
  // feature is broken rather than like the page could not read its state.
  const [policyError, setPolicyError] = useState(null);
  // The summary each card's credentials section last reported, keyed by channel.
  const [sectionStatus, setSectionStatus] = useState({});

  // Stable, and hands back the previous state when nothing changed, so a section repeating
  // the same summary costs no render.
  const reportStatus = useCallback((channel, next) => {
    setSectionStatus((prev) => (
      prev[channel]?.tone === next?.tone && prev[channel]?.text === next?.text
        ? prev
        : { ...prev, [channel]: next }
    ));
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiFetch('/api/channel-policy');
        if (resp.ok) {
          setPolicy(await resp.json());
        } else {
          const err = await resp.json().catch(() => ({}));
          setPolicyError(err.detail || `Failed to load coupon policy (${resp.status})`);
        }
      } catch {
        setPolicyError('Failed to load coupon policy');
      }
    })();
  }, []);

  const toggleFor = (channel, label) => (
    policyError
      ? (
        <div className="notice notice-danger m-0" role="alert">
          <Icon name="alertCircle" />
          <div className="notice-body">
            <div className="notice-title">Coupon activation cannot be changed right now</div>
            {policyError}
          </div>
        </div>
      )
      : <CouponToggle channel={channel} label={label} policy={policy}
                      onChange={setPolicy} />
  );

  // One card per channel the backend reports, in its order. This used to be two hardcoded
  // cards, so a channel added to KNOWN_CHANNELS got a policy entry the console never showed
  // — the toggle existed and was unreachable. The label comes from the backend too, because
  // the stored name and the operator-facing name differ: `website` is "RossHub" everywhere a
  // person reads it, and keeping that pairing here was a second copy of it.
  //
  // Two channels have credentials to manage here, and for different reasons. Telegram has a
  // POOL of bot tokens, one per bot, and users are load-balanced across them. Teams has a
  // pool of Azure Bot registrations keyed by App ID, plus a messaging endpoint to hand back
  // to Azure — it is the first channel where traffic arrives by webhook, so there is an
  // inbound URL to publish at all. RossHub is the remaining case: one platform credential
  // held in the environment, so nothing to manage beyond the coupon switch.
  const channels = Object.entries((policy && policy.channels) || {})
    .filter(([name]) => name !== 'voice' || phoneOn);
  const cards = channels.length
    ? channels.map(([name, c]) => [name, c.label || name])
    : [['telegram', 'Telegram']];

  return (
    <div>
      <PageHeader description="Where users reach their agent, and the credentials each channel needs.">
        <h1 className="page-title">Channels</h1>
      </PageHeader>

      <SectionStatus.Provider value={reportStatus}>
        {cards.map(([name, label]) => {
          const entry = policy?.channels?.[name];
          const status = sectionStatus[name];
          return (
            <section className="card" key={name} aria-labelledby={`channel-${name}-title`}>
              {/* The tip is per channel, and only Teams has one: it is the only channel whose
                  setup depends on values held in another console (Azure) where a mistake is
                  silent. Telegram and RossHub have nothing comparable to warn about. */}
              <div className="card-header">
                <div className="row-wrap gap-2">
                  <h2 className="card-title">
                    <Icon name={CHANNEL_ICON[name] || 'channels'} className="text-muted" />
                    {/* The id is on the name alone, so the card's landmark is not also
                        called "About Teams setup" after the tip button inside the heading. */}
                    <span id={`channel-${name}-title`}>{label}</span>
                    {name === 'teams' && <TeamsInfoTip />}
                  </h2>
                  {status && <Badge tone={status.tone} dot>{status.text}</Badge>}
                  {entry && name !== 'voice' && (
                    <Badge tone={entry.coupons_enabled ? 'ok' : 'neutral'} icon="coupons">
                      {entry.coupons_enabled ? 'Coupons on' : 'Coupons off'}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Nothing said on a call is read as a coupon code (proxy-channels'
                  handle_voice_message), so the phone has no coupon switch to offer. */}
              {name !== 'voice' && <div className="card-section">{toggleFor(name, label)}</div>}
              {name === 'voice' && (
                <div className="card-section">
                  <p className="row row-top gap-2 text-sm text-muted">
                    <Icon name="lock" />
                    <span>
                      A call reaches a user only from the one number allowed on their page
                      (Edit, Account), and runs on the models of their tier (Tiers, Phone
                      calls). The account auth token, the call timings and the prompts are
                      set on Tools, Voice, Phone calls, and nothing said on a call is read
                      as a coupon code.
                    </span>
                  </p>
                </div>
              )}

              {name === 'telegram' && <TelegramBotPool />}
              {name === 'teams' && <TeamsAppPool />}
              {APP_CHANNELS.includes(name) && <AppConnection channel={name} label={label} />}
              {name === 'website' && (
                <div className="card-section">
                  <p className="row row-top gap-2 text-sm text-muted">
                    <Icon name="lock" />
                    <span>
                      {label} uses one platform credential, set in the deployment environment,
                      so there is nothing else to configure here.
                    </span>
                  </p>
                </div>
              )}
              {name === 'website' && <WebsiteDeadLetters />}
            </section>
          );
        })}
      </SectionStatus.Provider>

      {/* Until the policy arrives only the fallback Telegram card is known; hold the place
          of the rest instead of letting them pop in under it. */}
      {!policy && !policyError && <CardSkeleton lines={3} />}
    </div>
  );
}

export default Channels;
